// © 2025 nSide, Inc. All Rights Reserved.
// This software and its source code are the confidential and proprietary
// information of nSide, Inc. (“Confidential Information”). You may not
// use, reproduce, disclose, distribute, modify, or create derivative works
// of this code except in accordance with a written license agreement or
// other written authorization from nSide, Inc.

// Unauthorized use is strictly prohibited and may result in civil and
// criminal penalties.



import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ApiService, DashboardData, DoorData, HistoryRecord, MapData } from '../../services/api.service';
import { AuthService } from '../../services/auth.service';
import { Loader } from '@googlemaps/js-api-loader';
import { SanitizeUrlPipe } from '../../pipes/sanitize-url.pipe';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, RouterModule, SanitizeUrlPipe],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit, OnDestroy {
  featureId: string = '';
  doorName: string = '';
  mapData: MapData | null = null;
  tableData: DoorData[] = [];
  googleApiKey: string = '';
  map: google.maps.Map | null = null;
  showHistoryModal = false;
  historyRecords: HistoryRecord[] = [];
  historyLoading = false;
  cameraFeedUrl: string | null = null;
  activeCameraFeatureId: string | null = null;

  dashboardLoading = true;
  unlockingDoors: Set<string> = new Set();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private authService: AuthService,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    // Extract JWT from query parameters if present
    this.route.queryParams.subscribe(queryParams => {
      const jwt = queryParams['jwt'];
      if (jwt) {
        this.authService.setToken(jwt);
        // Remove JWT from URL for security (clean the URL)
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {},
          replaceUrl: true
        });
      }
    });

    // Extract feature ID from route parameters
    this.route.params.subscribe(params => {
      this.featureId = params['featureId'];
      this.loadDashboardData();
    });
  }

  ngOnDestroy() {
    // Cleanup if needed
  }

  loadDashboardData() {
      this.dashboardLoading = true;
      this.apiService.getDashboardData(this.featureId).subscribe({
        next: (data: DashboardData) => {
          this.doorName = data.door_name;
          this.mapData = data.map_data;
          this.tableData = data.table_data;
          this.googleApiKey = data.google_api_key;
          this.initMap();
          this.dashboardLoading = false;
        },
        error: (error) => {
          this.dashboardLoading = false;
          console.error('Error loading dashboard data:', error);
          if (error.status === 403) {
            this.router.navigate(['/error'], { queryParams: { message: 'Access Denied. Please check your permissions' } });
          }
        }
      });
  }

  async initMap() {
    if (!this.mapData?.coords || !this.googleApiKey) return;

    const loader = new Loader({
      apiKey: this.googleApiKey,
      version: 'weekly'
    });

    try {
      await loader.load();
      const mapElement = document.getElementById('map-preview');
      if (!mapElement) return;

      this.map = new google.maps.Map(mapElement, {
        center: { lat: this.mapData.coords[0], lng: this.mapData.coords[1] },
        zoom: 18
      });

      // Add door markers
      if (this.mapData.all_doors) {
        this.mapData.all_doors.forEach(door => {
          const isSelected = door.featureId.toString() === this.featureId.toString();
          const marker = new google.maps.Marker({
            position: { lat: door.coords[0], lng: door.coords[1] },
            map: this.map!,
            icon: {
              url: isSelected ? '/assets/door_sensor_selected.png' : '/assets/door_sensor.png',
              scaledSize: new google.maps.Size(33, 40)
            },
            title: door.doorName
          });

          marker.addListener('click', () => {
            this.router.navigate(['/', door.featureId]);
            this.scrollTableToTop();
          });
        });
      }

      // Add room polygons
      if (this.mapData.room_geojson) {
        this.map.data.addGeoJson(JSON.parse(this.mapData.room_geojson));
        this.map.data.setStyle((feature) => {
          const fillColor = feature.getProperty('fillColor');
          return {
            fillColor: typeof fillColor === 'string' ? fillColor : '#cccccc',
            strokeColor: 'black',
            strokeWeight: 1,
            fillOpacity: 1.0
          };
        });
      }

      // Add room labels
      if (this.mapData.room_labels) {
        this.addRoomLabels(this.mapData.room_labels);
      }
    } catch (error) {
      console.error('Error loading Google Maps:', error);
    }
  }

  addRoomLabels(labels: { name: string; coords: [number, number] }[]) {
    if (!this.map) return;

    labels.forEach(label => {
      const overlay = new google.maps.OverlayView();
      const div = document.createElement('div');
      div.className = 'room-label';
      div.innerText = label.name;

      overlay.onAdd = function() {
        this.getPanes()!.overlayLayer.appendChild(div);
      };

      overlay.draw = () => {
        const projection = overlay.getProjection();
        const pos = projection.fromLatLngToDivPixel(new google.maps.LatLng(label.coords[0], label.coords[1]));
        if (pos) {
          div.style.left = pos.x + 'px';
          div.style.top = pos.y + 'px';
          const zoom = this.map!.getZoom() || 18;
          if (zoom < 21) {
            div.style.display = 'none';
          } else {
            div.style.display = 'block';
            div.style.fontSize = Math.max(8, zoom) + 'px';
          }
        }
      };

      overlay.onRemove = function() {
        if (div.parentNode) div.parentNode.removeChild(div);
      };

      overlay.setMap(this.map);

      if (this.map) {
        this.map.addListener('zoom_changed', () => {
          overlay.draw();
        });
      }
    });
  }

  unlockDoor(featureId: string) {
    this.unlockingDoors.add(featureId);
    this.apiService.unlockDoor(featureId).subscribe({
      next: () => {
        this.unlockingDoors.delete(featureId);
        this.toastr.success('Door unlocked!');
      },
      error: (error) => {
        this.unlockingDoors.delete(featureId);
        this.toastr.error('Error: ' + error.message);
      }
    });
  }

  isUnlocking(featureId: string): boolean {
    return this.unlockingDoors.has(featureId);
  }

  viewCamera(featureId: string) {
    this.apiService.viewCamera(featureId).subscribe({
      next: (feedUrl: string) => {
        if (feedUrl && feedUrl.startsWith('https://')) {
          this.cameraFeedUrl = feedUrl;
          this.activeCameraFeatureId = featureId;
        } else {
          this.toastr.warning('Camera feed not available.');
        }
      },
      error: () => {
        this.toastr.error('Error retrieving camera feed.');
      }
    });
  }

  closeCameraFeed() {
    this.cameraFeedUrl = null;
    this.activeCameraFeatureId = null;
  }

  viewHistory() {
    this.showHistoryModal = true;
    this.historyLoading = true;
    this.historyRecords = [];

    this.apiService.viewHistory(this.featureId).subscribe({
      next: (records: HistoryRecord[]) => {
        this.historyRecords = records;
        this.historyLoading = false;
      },
      error: () => {
        this.historyLoading = false;
        this.toastr.error('Error retrieving history records.');
      }
    });
  }

  closeHistoryModal() {
    this.showHistoryModal = false;
  }

  scrollTableToTop() {
    const container = document.querySelector('.door-table');
    if (container) {
      container.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  isSelectedDevice(featureId: string | number): boolean {
    return String(featureId).trim() === String(this.featureId).trim();
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString(undefined, { hour12: false }) + ' UTC';
  }

  get selectedDevice() {
    return this.tableData.find(row => String(row.featureId) === String(this.featureId));
  }
  
  openCameraFeed() {
    if (this.selectedDevice?.featureId) {
      this.viewCamera(this.selectedDevice.featureId);
    }
    this.scrollTableToTop();
  }
}


